import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CountrySelector, usePhoneInput, defaultCountries, parseCountry, buildCountryData } from 'react-international-phone';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { useIsAutofilled } from '@pattern-library/base';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
import useGoogleLibPhoneNumber from './useGoogleLibPhoneNumber';
const propTypes = {
  /**
   * **(international only)** The default country to select.
   * This should be an ISO 3166-1 alpha-2 country code.
   * See https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes for a list of valid country codes.
   */
  defaultCountry: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * **(international only)** Countries to exclude from the country selector dropdown.
   * This should be an array of country codes (ISO 3166-1 alpha-2 codes).
   * See https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes for a list of valid country codes.
   */
  excludeCountries: PropTypes.arrayOf(PropTypes.string),
  /**
   * render the component's error state
   */
  hasError: PropTypes.bool,
  /**
   * Disable masking on the input field
   */
  hideMask: PropTypes.bool,
  /**
   * **(international only)** Countries to include in the country selector dropdown.
   * This should be an array of country codes (ISO 3166-1 alpha-2 codes).
   * See https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes for a list of valid country codes.
   */
  includeCountries: PropTypes.arrayOf(PropTypes.string),
  /**
   * Input id
   **/
  id: PropTypes.string.isRequired,
  /**
   * Content for the the message popover component
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the message popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Initial phone number to display in the input
   */
  initialNumber: PropTypes.string,
  /**
   * Additional classes for the input element
   */
  inputClass: PropTypes.string,
  /**
   * Flag to indicate whether to allow international phone numbers.
   * When true, the country selector dropdown will be displayed.
   */
  isInternational: PropTypes.bool,
  /**
   * Flag to indicate whether this input is a required field
   */
  isRequired: PropTypes.bool,
  /**
   * The text label for the input
   */
  label: PropTypes.string,
  /**
   * Callback function to execute when the input value changes. <br />
   * The function will receive an object with the following properties: <br />
   * - `unformatted`: the unformatted phone number <br />
   * - `formatted`: the formatted phone number <br />
   * - `countryCode`: the ISO 3166-1 alpha-2 country code <br />
   * - `countryName`: the name of the country
   **/
  onChange: PropTypes.func,
  /**
   * Appended to the text label.
   * Defaults to (optional) for non-required inputs, but can be overridden.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * **(international only)** Countries to display at the top of the country selector dropdown.
   * This should be an array of country codes (ISO 3166-1 alpha-2 codes).
   * See https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes for a list of valid country codes.
   */
  preferredCountries: PropTypes.arrayOf(PropTypes.string),
  /**
   * Appends asterisk to label when the input is required.
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Text message shown below the input
   */
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
};
const defaultProps = {
  defaultCountry: 'us',
  excludeCountries: [],
  errorLevel: 'error',
  errorMessage: '',
  hasError: false,
  hideMask: false,
  includeCountries: [],
  infoPopoverContent: null,
  infoPopoverProps: {},
  initialNumber: '',
  inputClass: '',
  isRequired: true,
  isInternational: false,
  label: 'Phone number',
  optionalInputLabel: '(optional)',
  preferredCountries: [],
  showRequiredAsterisk: false
};
const PhoneInput = props => {
  // all props are defined here, whether they are used or not,
  // in order to prevent them from being passed onto the input
  const {
    defaultCountry,
    errorLevel,
    errorMessage,
    excludeCountries,
    hasError,
    hideMask,
    includeCountries,
    id,
    infoPopoverContent,
    infoPopoverProps,
    initialNumber,
    inputClass,
    isRequired,
    isInternational,
    label,
    onChange,
    optionalInputLabel,
    preferredCountries,
    showRequiredAsterisk,
    subtext,
    ...inputPassthrough
  } = props;
  const {
    GoogleLibPhoneNumber,
    phoneUtil
  } = useGoogleLibPhoneNumber();
  const isUSOnly = isInternational === false;
  const [mask, setMask] = useState('');
  const rootRef = useRef();
  const inputRef = useRef();
  const isAutofilled = useIsAutofilled(inputRef);
  const phoneInputClassNames = classNames('asc-phone-input', {
    [`asc-form-input--${errorLevel}`]: hasError
  });
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  const ariaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
  const replaceNumbersWithPeriods = str => str.replace(/[0-9]/g, '.');
  const transformFormat = format => {
    const removeParentheses = str => str.replace(/[()]/g, '');
    const replaceDashesWithSpaces = str => str.replace(/-/g, ' ');
    if (typeof format === 'string') {
      format = removeParentheses(format);
      format = replaceDashesWithSpaces(format);
    } else if (typeof format === 'object') {
      const newFormat = {
        ...format
      };
      Object.keys(format).forEach(key => {
        newFormat[key] = removeParentheses(newFormat[key]);
        newFormat[key] = replaceDashesWithSpaces(newFormat[key]);
      });
      return newFormat;
    }
    return format;
  };
  const defaultFormat = '............';
  /**
   * @param {string} inputValue
   * @param {import('react-international-phone').ParsedCountry['format']} [format]
   */
  function selectFormat(inputValue) {
    let format = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultFormat;
    if (typeof format === 'string') {
      return format;
    }
    for (const [pattern, fmt] of Object.entries(format)) {
      if (pattern === 'default') continue;
      if (new RegExp(pattern.slice(1, -1)).test(inputValue)) {
        return fmt;
      }
    }
    return format.default;
  }
  const countries = useMemo(() => {
    const getInitialPhoneNumberFormat = region => {
      const exampleNumber = phoneUtil.getExampleNumber(region);
      return replaceNumbersWithPeriods(phoneUtil.format(exampleNumber, GoogleLibPhoneNumber.PhoneNumberFormat.NATIONAL));
    };
    const selectedCountries = isUSOnly ? defaultCountries.filter(countryData => {
      const country = parseCountry(countryData);
      return country.iso2 === 'us';
    }) : defaultCountries.filter(countryData => {
      const iso2 = parseCountry(countryData).iso2;
      if (includeCountries.length > 0) {
        return includeCountries.includes(iso2);
      }
      return !excludeCountries.includes(iso2);
    });
    return selectedCountries.map(countryData => {
      let country = parseCountry(countryData);
      if (!country.format && phoneUtil) {
        country.format = getInitialPhoneNumberFormat(country.iso2.toLocaleUpperCase());
      }

      /*
          Mobile numbers in Argentina have a "9" between the country code
          and area code, leading to an 11-digit number in total. The default
          format for Argentina only supports 10 digits, so we add an alternate
          format here.
           See: https://github.com/goveo/react-international-phone/issues/167
       */
      if (country.iso2 === 'ar') {
        let format = typeof country.format === 'string' ? {
          default: country.format
        } : country.format;
        country.format = {
          ...format,
          '/^9/': '. .. ........'
        };
      }
      if (country.iso2 === 'us') {
        country.format = '...-...-....';
      } else if (country.format) {
        country.format = transformFormat(country.format);
      }
      return buildCountryData(country);
    });
  }, [isUSOnly, includeCountries, excludeCountries, phoneUtil, GoogleLibPhoneNumber]);
  const {
    country,
    inputValue,
    handlePhoneValueChange,
    setCountry
  } = usePhoneInput({
    defaultCountry: isUSOnly ? 'us' : defaultCountry,
    disableDialCodeAndPrefix: isUSOnly,
    value: initialNumber,
    inputRef: inputRef,
    countries,
    onChange: _ref => {
      let {
        phone,
        inputValue,
        country
      } = _ref;
      if (typeof onChange === 'function') {
        onChange({
          unformatted: phone,
          formatted: inputValue,
          countryCode: country.iso2,
          countryName: country.name
        });
      }
      if (hideMask) {
        return;
      }
      if (isInternational) {
        const dialCodePattern = /^\+(\d+) /;
        const dialCodeMatch = inputValue.match(dialCodePattern);
        if (!dialCodeMatch) {
          setMask('');
          return;
        }
        const dialCode = dialCodeMatch[1];
        if (dialCode !== country.dialCode) {
          setMask('');
          return;
        }
      }
      const localPhone = inputValue.replace(/^\+\d+ ?/, '');
      const format = selectFormat(localPhone, country.format);
      const remainingMask = format.slice(localPhone.length).replace(/\./g, '_');
      setMask(remainingMask);
    }
  });
  return /*#__PURE__*/React.createElement("div", {
    className: phoneInputClassNames,
    ref: rootRef
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    inputId: id,
    isRequired: isRequired,
    label: label,
    optionalInputLabel: optionalInputLabel,
    showRequiredAsterisk: showRequiredAsterisk
  }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  }), /*#__PURE__*/React.createElement("div", {
    className: classNames('asc-phone-input-wrapper', 'asc-form-input', inputClass)
  }, isInternational && /*#__PURE__*/React.createElement(CountrySelector, {
    id: 'country-select-' + id,
    countries: countries,
    preferredCountries: preferredCountries,
    selectedCountry: country.iso2,
    onSelect: _ref2 => {
      let {
        iso2
      } = _ref2;
      return setCountry(iso2);
    },
    buttonStyle: {
      borderLeft: 'none',
      borderRight: 'solid 1px #d8d8d8',
      borderTop: 'none',
      borderTopLeftRadius: '4px',
      borderBottom: 'none',
      height: '100%'
    },
    buttonContentWrapperStyle: {
      display: 'flex',
      justifyContent: 'center',
      width: '78px'
    },
    dropdownArrowClassName: "asc-dropdown-arrow",
    dropdownArrowStyle: {
      height: '15px',
      width: '15px',
      marginLeft: '5px',
      border: 'none'
    }
  }), /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: `${inputPassthrough.name ?? id ?? ''}CountryCode`,
    value: country.iso2
  }), /*#__PURE__*/React.createElement("div", {
    className: "asc-phone-input-mask"
  }, /*#__PURE__*/React.createElement("input", _extends({
    className: "asc-phone-input-input asc-form-input"
    // asc-form-input added as class for autoFill detection
    ,
    id: id,
    name: id,
    value: inputValue,
    onChange: handlePhoneValueChange,
    ref: inputRef,
    inputMode: "numeric",
    title: mask,
    "aria-describedby": ariaDescribedByIds.length ? ariaDescribedByIds.join(' ') : null
  }, inputPassthrough)), !hideMask && /*#__PURE__*/React.createElement("div", {
    className: classNames('asc-phone-input-mask__content', 'asc-phone-input-input', {
      'asc-phone-input-mask--with-autofill': isAutofilled
    }),
    "aria-hidden": "true",
    "data-testid": "mask"
  }, /*#__PURE__*/React.createElement("span", {
    className: "asc-phone-input-mask--content-hidden"
  }, inputValue), isInternational ? inputValue && mask : mask))), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext"
  }, subtext));
};
PhoneInput.propTypes = propTypes;
PhoneInput.defaultProps = defaultProps;
export default PhoneInput;